import dayjs from "dayjs";
import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import Chart from "./Chart";
import { DownloadCsvButtons } from "./DownloadCsvButtons";
import { InteractiveLineChartData, LocationKeywordData } from "./types";
import { transformKeywordData } from "./utils";
import { AccountType } from "../../../models/AccountData";
import {
  AnalysisChartFilterData,
  ChartData,
} from "../../../models/ChartCollectionData";
import { LocationData } from "../../../models/LocationData";
import { DateRangeArea } from "../../AnalysisCollectionsComponents/DateRangeArea";
import { OptionalRender } from "../../Common/OptionalRender";
import LocationSelectionModal from "../LocationSelectionModal";

import "./styles.scss";

export interface LocationKeywordsChartProps {
  data: LocationKeywordData[];
  width?: number | string;
  height?: number | string;
  onDateRangeChange?: (fromDate: string, toDate: string) => void;
  onLocationChange?: (locationIds: number[]) => void;
  canSelectionMultiLocations?: boolean;
  initLocationIds?: number[];
  initDateRange?: {
    fromDate: string;
    toDate: string;
  };
  showDownloadButtons?: boolean;
  chartContainerId?: string;
  organizationId?: string | number;
  locationId?: string | number;
  facilityName?: string;
}

const LocationKeywordsChart: React.FC<LocationKeywordsChartProps> = ({
  data,
  width = "100%",
  height = 400,
  onDateRangeChange,
  onLocationChange,
  canSelectionMultiLocations = false,
  initLocationIds = [],
  initDateRange,
  showDownloadButtons = true,
  chartContainerId,
  organizationId,
  locationId,
  facilityName,
}) => {
  const { t } = useTranslation();
  const [showLocationModal, setShowLocationModal] = useState<boolean>(false);
  const [selectedLocations, setSelectedLocations] = useState<LocationData[]>(
    []
  );
  const [resetSelectionFn, setResetSelectionFn] = useState<(() => void) | null>(
    null
  );

  const chartDataForDatePicker: ChartData = {
    id: 0,
    name: "",
    collection_id: 0,
    template_id: 0,
    template_name: "",
    period_start: initDateRange
      ? initDateRange.fromDate
      : dayjs().subtract(18, "month").format("YYYY-MM-DD"),
    period_end: initDateRange
      ? initDateRange.toDate
      : dayjs().format("YYYY-MM-DD"),
    period_option: "custom",
    type: "line",
    compare_to_prev: false,
    time_unit: "day",
    metrics: "",
    aggregation: "sum",
    grouping1: "",
    grouping2: "",
    prefecture_filter: "",
    municipality_filter: "",
    category_filter: "",
    label_filter: "",
    display_order: 0,
    is_template: true,
    created_at: "",
    created_by: 1,
    last_modified_at: "",
    last_modified_by: 1,
  };

  const handleDateRangeChange = (filterData: AnalysisChartFilterData) => {
    if (onDateRangeChange && filterData.fromDate && filterData.toDate) {
      const fromDate = dayjs(filterData.fromDate).format("YYYY-MM-DD");
      const toDate = dayjs(filterData.toDate).format("YYYY-MM-DD");

      onDateRangeChange(fromDate, toDate);

      if (resetSelectionFn) {
        resetSelectionFn();
      }
    }
  };

  const chartData: InteractiveLineChartData = useMemo(() => {
    const filteredData = data.map((location) => {
      const locationCopy = JSON.parse(JSON.stringify(location));
      return locationCopy;
    });

    return transformKeywordData(filteredData);
  }, [data]);

  const tooltipFormatter = (value: number | string) => {
    return new Intl.NumberFormat("ja").format(Number(value));
  };

  const handleLocationSelection = (locations: LocationData[]) => {
    setSelectedLocations(locations);
    setShowLocationModal(false);

    if (onLocationChange) {
      const locationIds = locations.map((loc) => loc.id);
      onLocationChange(locationIds);
    }
  };

  return (
    <div className="interactive-line-chart">
      <div className="interactive-chart-controls">
        <div className="top-keywords-filter">
          <OptionalRender
            mustBeOneOfRoles={[AccountType.ADMIN, AccountType.ORG_ADMIN]}
            mustBeTrue={canSelectionMultiLocations}
          >
            <button
              className="btn btn-border location-select-btn"
              onClick={() => setShowLocationModal(true)}
            >
              {t("facilityDetailsPage.interactiveLineChart.selectLocation")}
            </button>
          </OptionalRender>
        </div>
        <div className="chart-controls-row">
          {showDownloadButtons && (
            <DownloadCsvButtons
              data={data}
              dateFrom={dayjs(chartDataForDatePicker.period_start)}
              dateTo={dayjs(chartDataForDatePicker.period_end)}
              organizationId={organizationId}
              locationId={locationId}
              facilityName={facilityName}
            />
          )}
          {onDateRangeChange && (
            <div className="date-range-selector">
              <DateRangeArea
                chartData={chartDataForDatePicker}
                onClickApply={handleDateRangeChange}
                isDisableDayRangeSelect={true}
              />
            </div>
          )}
        </div>
      </div>

      <Chart
        data={chartData}
        width={width}
        height={height}
        tooltipFormatter={tooltipFormatter}
        onResetSelectionState={setResetSelectionFn}
        chartContainerId={chartContainerId}
      />

      {showLocationModal && (
        <LocationSelectionModal
          selectedLocations={selectedLocations}
          previousSelectedIds={[
            ...selectedLocations.map((loc) => loc.id),
            ...initLocationIds,
          ]}
          onClose={() => setShowLocationModal(false)}
          onApply={handleLocationSelection}
        />
      )}
    </div>
  );
};

export default LocationKeywordsChart;
